import React, { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Search as SearchIcon } from "lucide-react";
import api from "@/lib/api";
import { InterviewCard, OperatorCard, TopicCard, IndustryCard, CollectionCard, LoadingBlock } from "@/components/site/Cards";

export default function SearchPage() {
  const [sp, setSp] = useSearchParams();
  const [q, setQ] = useState(sp.get("q") || "");
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const query = sp.get("q");
    if (!query) {
      setResults(null);
      return;
    }
    setLoading(true);
    api.get("/search", { params: { q: query } })
      .then((r) => setResults(r.data))
      .finally(() => setLoading(false));
  }, [sp]);

  const submit = (e) => {
    e.preventDefault();
    if (q.trim()) setSp({ q: q.trim() });
  };

  return (
    <div data-testid="search-page">
      <section className="border-b border-oc-hairline">
        <div className="max-w-editorial mx-auto px-6 md:px-10 py-16 md:py-24">
          <div className="eyebrow text-oc-coral mb-6">Search</div>
          <h1 className="hero-headline text-5xl md:text-7xl text-oc-ink">Find something.</h1>
          <form onSubmit={submit} className="mt-10 flex items-center gap-3 max-w-2xl border-b-2 border-oc-ink pb-3">
            <SearchIcon className="w-6 h-6 text-oc-ink" />
            <input
              data-testid="search-input"
              autoFocus
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search interviews, operators, topics, industries..."
              className="flex-1 bg-transparent outline-none font-editorial text-2xl md:text-3xl text-oc-ink placeholder:text-oc-ink-muted"
            />
            <button data-testid="search-submit" className="font-ui text-xs font-bold uppercase tracking-widest text-oc-coral">
              Search
            </button>
          </form>
        </div>
      </section>

      <section className="max-w-editorial mx-auto px-6 md:px-10 py-16 space-y-16">
        {loading && <LoadingBlock />}
        {!loading && results && (
          <>
            <ResultGroup title="Interviews" count={results.interviews.length}>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {results.interviews.map((iv) => <InterviewCard key={iv.slug} interview={iv} />)}
              </div>
            </ResultGroup>
            <ResultGroup title="Operators" count={results.operators.length}>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {results.operators.map((op) => <OperatorCard key={op.slug} operator={op} />)}
              </div>
            </ResultGroup>
            <ResultGroup title="Topics" count={results.topics.length}>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {results.topics.map((t) => <TopicCard key={t.slug} topic={t} />)}
              </div>
            </ResultGroup>
            <ResultGroup title="Industries" count={results.industries.length}>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {results.industries.map((i) => <IndustryCard key={i.slug} industry={i} />)}
              </div>
            </ResultGroup>
            <ResultGroup title="Collections" count={results.collections.length}>
              <div className="space-y-6">
                {results.collections.map((c) => <CollectionCard key={c.slug} collection={c} />)}
              </div>
            </ResultGroup>
            {results.interviews.length + results.operators.length + results.topics.length + results.industries.length + results.collections.length === 0 && (
              <p className="py-24 text-center font-editorial text-xl text-oc-ink-muted">No results for "{sp.get("q")}".</p>
            )}
          </>
        )}
        {!loading && !results && (
          <div className="py-24 text-center font-editorial text-xl text-oc-ink-muted">
            Try searching for an operator name, an industry, or a topic.
          </div>
        )}
      </section>
    </div>
  );
}

function ResultGroup({ title, count, children }) {
  if (!count) return null;
  return (
    <div>
      <div className="flex items-baseline justify-between mb-8">
        <h2 className="font-editorial text-3xl text-oc-ink">{title}</h2>
        <div className="eyebrow text-oc-ink-muted">{count} result{count === 1 ? "" : "s"}</div>
      </div>
      {children}
    </div>
  );
}
